import Route from '@ember/routing/route';
import { service } from '@ember/service';
import type SessionService from 'ember-simple-auth/services/session';
import type { IntlService } from 'ember-intl';
import type Transition from '@ember/routing/transition';
import Intercom from '@intercom/messenger-js-sdk';
import config from 'dashboard/config/environment';
import { isTesting, macroCondition } from '@embroider/macros';
import { chooseBestFitLocale } from '@showbie/utilities';
import { SUPPORTED_LOCALES } from 'dashboard/constants';
import type FeaturesService from '@showbie/feature-flags-ember/services/features';
import breakpoints from 'dashboard/breakpoints';
import type MediaService from 'ember-media-query/services/media';

// https://www.npmjs.com/package/@intercom/messenger-js-sdk#installation
const INTERCOM_SETTINGS = {
  api_base: 'https://api-iam.intercom.io',
  app_id: config.intercom.app_id,
};

if (macroCondition(!isTesting())) {
  Intercom(INTERCOM_SETTINGS);
}

export default class ApplicationRoute extends Route {
  @service declare features: FeaturesService;
  @service declare intl: IntlService;
  @service declare media: MediaService;
  @service declare session: SessionService;

  async beforeModel(transition: Transition): Promise<void> {
    super.beforeModel(transition);

    // define `ember-media-query` breakpoints
    this.media.setBreakpoints(breakpoints);

    // load feature flags
    await this.features.setup(config.featureFlags);
    // https://github.com/simplabs/ember-simple-auth/blob/master/guides/upgrade-to-v4.md
    await this.session.setup();

    let locale = 'en-us';
    if (!this.session.isAuthenticated) {
      // choose locale based on browser settings for non-authenticated users
      locale = chooseBestFitLocale(SUPPORTED_LOCALES);
    }

    // https://ember-intl.github.io/ember-intl/versions/v5.7.0/docs/quickstart#4-configure-ember-intl
    this.intl.setLocale([locale]);
  }
}
